import React from 'react';
import { Box, Typography, Button, Container, Card, CardContent } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Users,
  UserCheck,
  ExternalLink,
  GraduationCap,
  Briefcase,
  FileText,
  ClipboardList,
  BookOpen,
  Workflow,
  LayoutDashboard,
  Database,
  Layers,
  Network,
  Bot,
  Rocket,
  Target,
  Award,
  MessageSquare,
  Linkedin,
  Clock,
  IndianRupee,
  Gift,
} from 'lucide-react';

const REGISTRATION_FORM_URL = 'https://forms.gle/Wi3JDoUkpz75zriA9';

const PRIMARY_BLUE = '#2563EB';
const PRIMARY_BLUE_LIGHT = '#EAF2FF';
const GREEN = '#22c55e';
const TEXT_DARK = '#1e293b';
const TEXT_MUTED = '#64748b';
const BORDER = '#e5e7eb';
const BG_LIGHT = '#f8fafc';

const heroPoints = [
  'No Coding Required',
  '2 Months Program',
  '₹4,999 Course Fee',
  'Live Online + Recordings',
  'Certificate + Placement Assistance',
];

const stats = [
  { value: '8', label: 'Course Modules' },
  { value: '8', label: 'Weeks Live Training' },
  { value: '8+', label: 'Hands-on Projects' },
  { value: '₹4,999', label: 'Full Course Fee' },
  { value: '100%', label: 'Career Support' },
];

const whatYouGet = [
  'Learn the complete Business Analyst workflow',
  'Work on real-time projects and industry case studies',
  'Get resume, LinkedIn and Naukri profile ready',
  'Prepare for interviews with mock sessions',
];

const modules = [
  { icon: <BookOpen size={20} color={PRIMARY_BLUE} />, module: 'Module 1', title: 'Business Analysis Fundamentals', items: ['Introduction to Business Analysis', 'BA Roles & Responsibilities', 'SDLC & STLC', 'Agile & Scrum', 'Waterfall Methodology', 'Software Development Lifecycle'] },
  { icon: <ClipboardList size={20} color={PRIMARY_BLUE} />, module: 'Module 2', title: 'Requirement Engineering', items: ['Requirement Gathering', 'Stakeholder Management', 'User Stories', 'BRD', 'FRD', 'SRS', 'Use Cases', 'Acceptance Criteria'] },
  { icon: <Workflow size={20} color={PRIMARY_BLUE} />, module: 'Module 3', title: 'Process Modeling', items: ['BPMN', 'Process Flow Diagrams', 'UML Basics', 'Swimlane Diagrams', 'Gap Analysis', 'As-Is & To-Be Process'] },
  { icon: <LayoutDashboard size={20} color={PRIMARY_BLUE} />, module: 'Module 4', title: 'Tools Every BA Must Know', items: ['Jira', 'Confluence', 'Microsoft Visio', 'Draw.io', 'Lucidchart', 'Figma Basics', 'Excel for Business Analysts'] },
  { icon: <Database size={20} color={PRIMARY_BLUE} />, module: 'Module 5', title: 'SQL for Business Analysts', items: ['Database Basics', 'SQL Queries', 'Joins', 'Aggregate Functions', 'Views', 'Subqueries', 'Data Analysis'] },
  { icon: <Layers size={20} color={PRIMARY_BLUE} />, module: 'Module 6', title: 'Agile Business Analyst', items: ['Scrum Framework', 'Sprint Planning', 'Backlog Grooming', 'User Story Writing', 'Story Estimation', 'Product Backlog'] },
  { icon: <Network size={20} color={PRIMARY_BLUE} />, module: 'Module 7', title: 'API & Integration Basics', items: ['REST APIs', 'JSON', 'Postman', 'API Testing', 'Authentication Basics'] },
  { icon: <Bot size={20} color={PRIMARY_BLUE} />, module: 'Module 8', title: 'AI for Business Analysts', items: ['Introduction to Generative AI', 'AI Tools for Business Analysts', 'Prompt Engineering', 'Requirement Generation using AI', 'Documentation with AI', 'Productivity Automation'] },
];

const audience = [
  'Fresh Graduates',
  'MBA Students',
  'BBA / B.Com / BCA / B.Sc Students',
  'Working Professionals',
  'Career Switchers',
  'Non-IT Professionals',
  'QA, Support & Operations Staff',
  'Domain Experts (BFSI, Healthcare, Retail)',
  'Anyone looking to enter the IT industry',
];

const careerSteps = [
  'Professional Resume Building',
  'ATS-Friendly Resume',
  'Industry Standard Resume',
  'Resume Review & Guidance',
  'LinkedIn Profile Optimization',
  'Naukri Profile Optimization',
  'Interview Preparation & Mock Interviews',
  'Communication Skills & Salary Negotiation',
];

const careerCards = [
  { icon: <Briefcase size={22} color={PRIMARY_BLUE} />, title: 'Business Analyst Skills', description: 'Requirement gathering, stakeholder management, process modeling, and business analysis frameworks.' },
  { icon: <FileText size={22} color={PRIMARY_BLUE} />, title: 'Resume & Profile Optimization', description: 'ATS-friendly resumes, LinkedIn headlines, and Naukri profiles that attract recruiters.' },
  { icon: <MessageSquare size={22} color={PRIMARY_BLUE} />, title: 'Mock Interviews & Scenarios', description: 'HR questions, BA case studies, and live interview practice sessions.' },
  { icon: <Users size={22} color={PRIMARY_BLUE} />, title: 'Placement Assistance', description: 'Job referrals, interview scheduling help, and career guidance support.' },
];

const projects = [
  'Requirement Gathering Project',
  'BRD & FRD Documentation',
  'User Story Creation',
  'Process Flow Design',
  'SQL Assignment',
  'Agile Sprint Simulation',
  'API Testing Project',
  'Final Capstone Project',
];

const placement = [
  { icon: <FileText size={20} color={PRIMARY_BLUE} />, title: 'Resume Review' },
  { icon: <Linkedin size={20} color={PRIMARY_BLUE} />, title: 'LinkedIn Review' },
  { icon: <LayoutDashboard size={20} color={PRIMARY_BLUE} />, title: 'Naukri Profile Review' },
  { icon: <MessageSquare size={20} color={PRIMARY_BLUE} />, title: 'Mock Interviews' },
  { icon: <Briefcase size={20} color={PRIMARY_BLUE} />, title: 'Job Referrals' },
  { icon: <Clock size={20} color={PRIMARY_BLUE} />, title: 'Interview Scheduling Support' },
  { icon: <Target size={20} color={PRIMARY_BLUE} />, title: 'Career Guidance Sessions' },
  { icon: <Users size={20} color={PRIMARY_BLUE} />, title: 'Placement Community Access' },
];

const highlights = [
  '8 Weeks Live Training',
  'Weekend & Weekday Batches',
  'Recorded Sessions',
  'Real-Time Projects',
  'Assignments',
  'Industry Templates',
  'Interview Preparation',
  'Placement Assistance',
  'Certificate of Completion',
  'Lifetime Learning Community',
];

const whyChoose = [
  'Business Analyst Skills',
  'Real-Time Projects',
  'Industry Case Studies',
  'Resume Preparation',
  'LinkedIn Profile Optimization',
  'Naukri Profile Optimization',
  'Mock Interviews',
  'Placement Support',
  'Soft Skills Training',
  'Interview Questions & Answers',
  'Lifetime Community Access',
];

const SectionTitle: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box sx={{ textAlign: 'center', mb: 6 }}>
    <Typography
      component="h2"
      sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' }, fontWeight: 800, color: TEXT_DARK, lineHeight: 1.25 }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ color: TEXT_MUTED, fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 750, mx: 'auto', mt: 2, lineHeight: 1.8 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const BusinessAnalystCareerAccelerator: React.FC = () => {
  return (
    <Box sx={{ bgcolor: BG_LIGHT, pt: { xs: 7, md: 8 } }}>
      {/* HERO — content left, image right */}
      <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 4 }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} sx={{ alignItems: 'center' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Box sx={{ bgcolor: PRIMARY_BLUE_LIGHT, color: PRIMARY_BLUE, px: 2.5, py: 0.75, borderRadius: '20px', fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rocket size={16} />
                    Business Analyst Career Accelerator Program
                  </Box>
                </Box>

                <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' }, fontWeight: 800, lineHeight: 1.3, mb: 2, color: TEXT_DARK }}>
                  Launch Your Career as a Business Analyst
                </Typography>

                <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: PRIMARY_BLUE, lineHeight: 1.6, mb: 3, fontWeight: 600 }}>
                  A 2-Month Job-Ready Program with Real Projects, Interview Preparation & Placement Assistance
                </Typography>

                <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, color: TEXT_MUTED, lineHeight: 1.8, mb: 3 }}>
                  Whether you're a fresher, working professional, or career switcher, this program is designed to make you
                  job-ready — from Business Analysis concepts and industry tools to resume building, LinkedIn branding,
                  interview preparation, and placement support.
                </Typography>

                <Box sx={{ mb: 3 }}>
                  {heroPoints.map((item, i) => (
                    <motion.div key={item} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                        <CheckCircle size={20} color={GREEN} />
                        <Typography sx={{ color: TEXT_DARK, fontSize: '0.95rem', fontWeight: 500 }}>{item}</Typography>
                      </Box>
                    </motion.div>
                  ))}
                </Box>

                <Box sx={{ bgcolor: '#ecfdf5', border: `2px solid ${GREEN}`, borderRadius: '14px', p: 2, mb: 3, display: 'inline-block' }}>
                  <Typography sx={{ color: '#16a34a', fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GraduationCap size={22} /> Admissions Open — Limited Seats per Batch!
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flexWrap: 'wrap' }}>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button
                      variant="contained"
                      href={REGISTRATION_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<ExternalLink size={20} />}
                      sx={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', color: '#fff', fontSize: { xs: '1rem', md: '1.1rem' }, fontWeight: 700, textTransform: 'none', px: { xs: 4, md: 5 }, py: { xs: 1.5, md: 1.75 }, borderRadius: '14px', boxShadow: '0 8px 32px rgba(37, 99, 235, 0.35)', '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)', boxShadow: '0 12px 40px rgba(37, 99, 235, 0.45)' } }}
                    >
                      Enroll Now
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {/* Mobile */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1000&q=80"
                  alt="Business Analyst Career Accelerator Program"
                  sx={{ width: '100%', height: 'auto', borderRadius: '16px', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' }}
                />
              </Box>

              {/* Desktop */}
              <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative', height: 460 }}>
                <Box sx={{ position: 'absolute', top: '8%', left: '8%', width: 110, height: 110, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)', zIndex: 0 }} />
                <Box sx={{ position: 'absolute', bottom: '10%', right: '12%', width: 70, height: 70, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)', zIndex: 0 }} />

                <motion.div
                  initial={{ opacity: 0, x: 40, rotate: 3 }}
                  animate={{ opacity: 1, x: 0, rotate: 3 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=900&q=80"
                    alt="Business Analyst training session"
                    sx={{ width: 380, height: 260, objectFit: 'cover', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.2)', border: '4px solid #fff' }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -40, rotate: -4 }}
                  animate={{ opacity: 1, x: 0, rotate: -4 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  style={{ position: 'absolute', left: 0, bottom: 0, zIndex: 2 }}
                >
                  <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=900&q=80"
                    alt="Business analysis and reporting"
                    sx={{ width: 320, height: 230, objectFit: 'cover', borderRadius: '16px', boxShadow: '0 25px 60px rgba(37, 99, 235, 0.25)', border: '4px solid #fff' }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.7 }}
                  style={{ position: 'absolute', right: '12%', bottom: '18%', zIndex: 3 }}
                >
                  <Box sx={{ bgcolor: '#fff', borderRadius: '50%', width: 92, height: 92, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.15)', border: `3px solid ${PRIMARY_BLUE}` }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY_BLUE, lineHeight: 1 }}>2</Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase' }}>Months</Typography>
                  </Box>
                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* STATS */}
      <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 4 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat) => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={stat.label}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Box sx={{ textAlign: 'center', p: 3, bgcolor: BG_LIGHT, borderRadius: '16px' }}>
                    <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: PRIMARY_BLUE }}>{stat.value}</Typography>
                    <Typography sx={{ color: TEXT_MUTED, fontWeight: 500 }}>{stat.label}</Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* VIDEO OVERVIEW */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: BG_LIGHT }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle
              title="Watch the Program Overview"
              subtitle="See how the 2-month program prepares you with business analysis skills, real projects, placement support, and career-ready coaching."
            />
            <Box sx={{ position: 'relative', pt: '56.25%', borderRadius: '16px', overflow: 'hidden', border: `1px solid ${BORDER}`, boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
              <Box
                component="iframe"
                src="https://www.youtube.com/embed/p7vU4ZF4gGo"
                title="Business Analyst Career Accelerator Program"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
              />
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* WHAT IS THE PROGRAM */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Grid container spacing={6} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, color: TEXT_DARK, mb: 3 }}>
                  A 2-Month Career-Focused Business Analyst Course
                </Typography>
                <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 3 }}>
                  This is not just another technical course. We prepare you with the complete set of skills required to secure
                  an IT job — from Business Analysis concepts and industry tools to resume building, LinkedIn branding,
                  interview preparation, and placement support.
                </Typography>
                <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: '12px', border: `1px solid ${BORDER}` }}>
                  {whatYouGet.map((item) => (
                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                      <CheckCircle size={20} color={GREEN} />
                      <Typography sx={{ color: TEXT_DARK, fontWeight: 500 }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ mt: 3, color: PRIMARY_BLUE, fontWeight: 700, fontSize: '1rem' }}>
                  Designed for beginners — no programming background is required.
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card sx={{ borderRadius: '16px', border: `1px solid ${BORDER}`, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: TEXT_DARK, mb: 2.5 }}>
                      Why Choose This Program?
                    </Typography>
                    <Grid container spacing={1.5}>
                      {whyChoose.map((item) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={item}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                            <CheckCircle size={18} color={GREEN} />
                            <Typography sx={{ color: TEXT_DARK, fontSize: '0.92rem', fontWeight: 500 }}>{item}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* COURSE STRUCTURE */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: BG_LIGHT }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle
              title="What You'll Learn"
              subtitle="A complete Business Analyst roadmap from fundamentals to AI-powered productivity."
            />
            <Grid container spacing={4}>
              {modules.map((m) => (
                <Grid size={{ xs: 12, md: 6 }} key={m.title}>
                  <motion.div whileHover={{ y: -8 }}>
                    <Card sx={{ height: '100%', border: `1px solid ${BORDER}`, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: '16px' }}>
                      <Box sx={{ bgcolor: '#fff', py: 3, px: 3, borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: PRIMARY_BLUE_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {m.icon}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: PRIMARY_BLUE }}>{m.module}</Typography>
                          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: TEXT_DARK, lineHeight: 1.35 }}>{m.title}</Typography>
                        </Box>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        {m.items.map((item, j) => (
                          <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: j < m.items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                            <CheckCircle size={18} color={PRIMARY_BLUE} />
                            <Typography sx={{ color: TEXT_DARK, fontWeight: 500 }}>{item}</Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* WHO CAN JOIN */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle title="Who Should Join?" subtitle="This course is perfect for anyone planning a career in the IT industry." />
            <Grid container spacing={2}>
              {audience.map((who) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={who}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#fff', borderRadius: '12px', border: `1px solid ${BORDER}` }}>
                    <UserCheck size={24} color={PRIMARY_BLUE} />
                    <Typography sx={{ fontWeight: 600, color: TEXT_DARK }}>{who}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography sx={{ color: GREEN, fontWeight: 700, fontSize: '1.1rem' }}>
                No coding. No technical background required.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* CAREER DEVELOPMENT PROGRAM */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: BG_LIGHT }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle title="Career Development Program" subtitle="This is where we make you interview-ready." />
            <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card sx={{ height: '100%', border: `1px solid ${BORDER}`, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: '16px' }}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    {careerSteps.map((step, i) => (
                      <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.75, borderBottom: i < careerSteps.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: PRIMARY_BLUE_LIGHT, color: PRIMARY_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
                          {i + 1}
                        </Box>
                        <Typography sx={{ color: TEXT_DARK, fontWeight: 500 }}>{step}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={2}>
                  {careerCards.map((card) => (
                    <Grid size={{ xs: 12 }} key={card.title}>
                      <motion.div whileHover={{ y: -6 }}>
                        <Card sx={{ border: `1px solid ${BORDER}`, boxShadow: 'none', borderRadius: '16px', '&:hover': { borderColor: PRIMARY_BLUE, boxShadow: '0 12px 40px rgba(37, 99, 235, 0.12)' } }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                              <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: PRIMARY_BLUE_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {card.icon}
                              </Box>
                              <Typography sx={{ fontWeight: 700, color: TEXT_DARK }}>{card.title}</Typography>
                            </Box>
                            <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.75, fontSize: '0.95rem' }}>{card.description}</Typography>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* HANDS-ON PROJECTS */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle
              title="Hands-on Projects"
              subtitle="Build skills through practical deliverables aligned to real business scenarios."
            />
            <Grid container spacing={3}>
              {projects.map((project, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={project}>
                  <motion.div whileHover={{ y: -8 }}>
                    <Card sx={{ height: '100%', border: `1px solid ${BORDER}`, boxShadow: 'none', borderRadius: '16px', '&:hover': { borderColor: PRIMARY_BLUE, boxShadow: '0 12px 40px rgba(37, 99, 235, 0.12)' } }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: PRIMARY_BLUE, mb: 1 }}>
                          {String(i + 1).padStart(2, '0')}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: TEXT_DARK }}>{project}</Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* PLACEMENT ASSISTANCE */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: PRIMARY_BLUE_LIGHT }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle title="Placement Assistance" subtitle="Our support continues even after training." />
            <Grid container spacing={2}>
              {placement.map((item) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.title}>
                  <motion.div whileHover={{ scale: 1.03 }}>
                    <Box sx={{ bgcolor: '#fff', px: 3, py: 2.25, borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', height: '100%' }}>
                      {item.icon}
                      <Typography sx={{ fontWeight: 600, color: TEXT_DARK, fontSize: '0.95rem' }}>{item.title}</Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* PROGRAM HIGHLIGHTS */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <SectionTitle title="Program Highlights" />
            <Grid container spacing={2} justifyContent="center">
              {highlights.map((item) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: BG_LIGHT, borderRadius: '12px' }}>
                    <CheckCircle size={20} color={GREEN} />
                    <Typography sx={{ fontWeight: 600, color: TEXT_DARK, fontSize: '0.95rem' }}>{item}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* WHY REVIVING INDIA */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: BG_LIGHT }}>
        <Container maxWidth="lg">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Grid container spacing={6} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography component="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, color: TEXT_DARK, mb: 3 }}>
                  Why Reviving India?
                </Typography>
                <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 2.5 }}>
                  Reviving India has trained students and professionals through industry-focused programs designed to bridge the
                  gap between learning and employment.
                </Typography>
                <Typography sx={{ color: PRIMARY_BLUE, fontWeight: 700, fontSize: '1.05rem', mb: 2.5 }}>
                  Our goal is simple: Teach. Build. Prepare. Place.
                </Typography>
                <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8 }}>
                  We focus on practical learning, real-world projects, career guidance, and interview readiness so that every
                  learner is equipped for today's IT industry.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: PRIMARY_BLUE_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                      <Award size={24} color={PRIMARY_BLUE} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: TEXT_DARK, mb: 1.5 }}>
                      Ready to Start Your Business Analyst Career?
                    </Typography>
                    <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 3 }}>
                      Join hundreds of learners who are preparing for successful careers in the IT industry.
                    </Typography>
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        href={REGISTRATION_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<ExternalLink size={20} />}
                        sx={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', color: '#fff', fontSize: '1rem', fontWeight: 700, textTransform: 'none', py: 1.6, borderRadius: '14px', boxShadow: '0 8px 32px rgba(37, 99, 235, 0.35)', '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)' } }}
                      >
                        Enroll Now
                      </Button>
                    </motion.div>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* PRICING & CTA */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 }, background: 'linear-gradient(135deg, #030712 0%, #0f172a 50%, #030712 100%)' }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Box sx={{ textAlign: 'center', color: '#fff' }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1, opacity: 0.9 }}>
                <IndianRupee size={16} style={{ verticalAlign: 'middle' }} /> AFFORDABLE CAREER INVESTMENT
              </Typography>
              <Typography component="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, mb: 2 }}>
                Just ₹4,999 for 2 Months
              </Typography>
              <Typography sx={{ fontSize: '1.2rem', opacity: 0.9, mb: 4 }}>
                Duration: 2 Months | Mode: Live Online + Recordings | Level: Beginner to Job-Ready
              </Typography>

              <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '16px', p: 3, mb: 4, border: '1px dashed rgba(255,255,255,0.3)' }}>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Gift size={24} /> Placement Assistance Included
                </Typography>
              </Box>

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  href={REGISTRATION_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ExternalLink size={22} />}
                  sx={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)', color: '#fff', fontSize: '1.2rem', fontWeight: 800, textTransform: 'none', px: 6, py: 2, borderRadius: '16px', boxShadow: '0 8px 30px rgba(37, 99, 235, 0.4)', '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)', boxShadow: '0 12px 40px rgba(37, 99, 235, 0.5)' } }}
                >
                  Enroll Now - Start Your BA Career
                </Button>
              </motion.div>

              <Typography sx={{ mt: 3, opacity: 0.8, fontSize: '0.9rem' }}>
                We don't just teach Business Analysis — We make you Job Ready + Career Ready + Future Ready.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default BusinessAnalystCareerAccelerator;
